import { map } from 'lodash-es';

export default {

  /**
   * @param state
   * @returns {Array}
   */
  getSeasonsWithRelations(state) {
    return map(state.seasons.items, season => ({
      id: season.id,
      twentySupplierId: season.twentySupplierId,
      salesRule: season.salesRule,
      name: season.name,
      arrivalFrom: season.arrivalFrom,
      arrivalTo: season.arrivalTo,
      netOrGross: season.netOrGross,
      active: season.active,
      webActive: season.webActive,
      bookDaysBeforeArrival: season.bookDaysBeforeArrival,
      commissionRatio: season.commissionRatio,
      contributionRatio: season.contributionRatio,
      numPersonCategories: Object.keys(season.personCategories).length,
      numLevels: Object.keys(season.seasonLevels).length,
      numPrices: Object.keys(season.prices).length,
      numAreas: Object.keys(season.seasonAreas).length,
      numPickupPlaces: 0,
    }));
  },

  /**
   * @param state
   * @returns {{priceSettings: *, numPrices: *, numAreas: *, webActive: *, numPersonCategories: *,
   * name: *, active: *, twentySupplierId: *, numLevels: *, id: *, netOrGross: *}}
   */
  getSeasonWithRelations(state) {
    const season = state.season;
    const pickupPlaces = state.pickupPlaces.items;

    return {
      id: season.id,
      twentySupplierId: season.twentySupplierId,
      salesRule: season.salesRule,
      name: season.name,
      arrivalFrom: season.arrivalFrom,
      arrivalTo: season.arrivalTo,
      netOrGross: season.netOrGross,
      active: season.active,
      webActive: season.webActive,
      bookDaysBeforeArrival: season.bookDaysBeforeArrival,
      commissionRatio: season.commissionRatio,
      contributionRatio: season.contributionRatio,
      numPersonCategories: Object.keys(season.personCategories.items).length,
      numLevels: Object.keys(season.levels.items).length,
      numPrices: Object.keys(season.prices.items).length,
      numAreas: Object.keys(season.skiAreas.ids).length,
      numPickupPlaces: pickupPlaces.filter(pickupPlace => pickupPlace.selected).length,
    };
  },

  /**
   * @param state
   * @returns {Array}
   */
  getPersonCategories(state) {
    let personCategories = state.season.personCategories.items;
    if (!personCategories || !Object.keys(personCategories).length) {
      return [];
    }
    personCategories = map(state.season.personCategories.items, personCategory => ({
      id: personCategory.id,
      skiSchoolSeasonId: personCategory.skiSchoolSeasonId,
      name: personCategory.name,
      externalName: personCategory.externalName,
      ageFrom: personCategory.ageFrom,
      ageTo: personCategory.ageTo,
      xmlChannelProductId: personCategory.xmlChannelProductId,
      domains: personCategory.domains,
    }));

    return personCategories.sort((a, b) => a.ageFrom - b.ageFrom);
  },

  /**
   * @param state
   * @returns {Array}
   */
  getLevels(state) {
    const stateLevels = state.season.levels.items;
    if (!stateLevels || !Object.keys(stateLevels).length) {
      return [];
    }
    const levels = map(stateLevels, stateLevel => ({
      id: stateLevel.id,
      skiSchoolSeasonId: stateLevel.skiSchoolSeasonId,
      name: stateLevel.name,
      externalName: stateLevel.externalName,
      priority: stateLevel.priority,
      domains: stateLevel.domains || {},
    }));

    return levels.sort((a, b) => a.priority - b.priority);
  },

  /**
   * @param state
   * @param getters
   * @returns {*|Function}
   */
  getPersonCategoriesWithAllOption(state, getters) {
    return getters.mapPersonCategoriesWithReadableNames(state);
  },

  /**
   * @returns {Function}
   */
  mapPersonCategoriesWithReadableNames: () => state => {
    let personCategories = state.season.personCategories.items;
    if (!personCategories || !Object.keys(personCategories).length) {
      return [];
    }
    personCategories = map(state.season.personCategories.items, personCategory => ({
      id: personCategory.id,
      skiSchoolSeasonId: personCategory.skiSchoolSeasonId,
      nameWithAges: personCategory.name.concat(' (')
        .concat(personCategory.ageFrom)
        .concat('-')
        .concat(personCategory.ageTo)
        .concat(' years)'),
      name: personCategory.name,
      externalName: personCategory.externalName,
      ageFrom: personCategory.ageFrom,
      ageTo: personCategory.ageTo,
    }));

    return personCategories.sort((a, b) => a.ageFrom - b.ageFrom);
  },

  /**
   * @param state
   * @returns {this|Array}
   */
  getSkiSuppliers(state) {
    const stateSuppliers = state.suppliers.items;
    if (!stateSuppliers || !Object.keys(stateSuppliers).length) {
      return [];
    }
    const suppliers = map(stateSuppliers, supplier => ({
      id: supplier.id,
      name: supplier.name,
    }));

    return suppliers.sort((a, b) => a.name.localeCompare(b.name));
  },

  /**
   * @param state
   * @returns {this|Array}
   */
  getSalesTypes(state) {
    const stateSalesTypes = state.salesTypes.items;
    if (!stateSalesTypes || !Object.keys(stateSalesTypes).length) {
      return [];
    }
    const salesTypes = map(stateSalesTypes, salesTypeIdentifier => {
      let salesTypeName = '';
      switch (salesTypeIdentifier) {
        case 'booking':
          salesTypeName = 'Pay now (default)';
          break;
        case 'bookingArrival':
          salesTypeName = 'Book & pay upon arrival';
          break;
        case 'arrival':
          salesTypeName = 'Pay upon arrival';
          break;
        default:
          salesTypeName = salesTypeIdentifier;
          break;
      }
      return {
        id: salesTypeIdentifier,
        name: salesTypeName,
      };
    });

    return salesTypes.sort((a, b) => a.name.localeCompare(b.name));
  },

  /**
   * @param state
   * @returns {this|Array}
   */
  getLessonTypes(state) {
    const stateLessonTypes = state.lessonTypes.items;
    if (!stateLessonTypes || !Object.keys(stateLessonTypes).length) {
      return [];
    }
    const lessonTypes = map(stateLessonTypes, lessonType => ({
      id: lessonType.id,
      name: lessonType.name,
      identifier: lessonType.identifier,
      priority: lessonType.priority,
    }));

    return lessonTypes.sort((a, b) => a.priority - b.priority);
  },

  /**
   * @param state
   * @returns {this|Array}
   */
  getSkiTypes(state) {
    const stateSkiTypes = state.skiTypes.items;
    if (!stateSkiTypes || !Object.keys(stateSkiTypes).length) {
      return [];
    }
    const skiTypes = map(stateSkiTypes, skiType => ({
      id: skiType.id,
      name: skiType.name,
      identifier: skiType.identifier,
      priority: skiType.priority,
    }));

    return skiTypes.sort((a, b) => a.priority - b.priority);
  },

  /**
   * @param state
   * @param getters
   * @returns {this|Array}
   */
  getPricesWithNames(state, getters) {
    const statePrices = state.season.prices.items;
    const stateLessonTypes = state.lessonTypes.items;
    const stateSkiTypes = state.skiTypes.items;
    if (!statePrices
      || !Object.keys(statePrices).length
      || !stateLessonTypes
      || !Object.keys(stateLessonTypes).length
      || !stateSkiTypes
      || !Object.keys(stateSkiTypes).length
    ) {
      return [];
    }

    const statePersonCategories = getters.mapPersonCategoriesWithReadableNames(state);
    const stateLevels = state.season.levels.items;
    const pricesWithNames = map(statePrices, statePrice => {
      const priceCombinations = map(statePrice.priceCombinations, priceCombination => {
        const personCategory = statePersonCategories.find(
          statePersonCategory => statePersonCategory.id
            === priceCombination.skiSchoolSeasonPersonCategoryId,
        );
        const level = stateLevels.find(
          stateLevel => stateLevel.id === priceCombination.skiSchoolSeasonLevelId,
        );

        let areaPickupSelected = state.pickupPlaces.items.find(
          areaPickup => areaPickup.id === priceCombination.areaPickupId,
        );
        if (!areaPickupSelected) {
          areaPickupSelected = { title: 'No pickup place' };
        }

        return {
          id: priceCombination.id,
          skiSchoolSeasonPriceId: priceCombination.skiSchoolSeasonPriceId,
          skiSchoolSeasonLevelId: priceCombination.skiSchoolSeasonLevelId,
          skiSchoolSeasonPersonCategoryId: priceCombination.skiSchoolSeasonPersonCategoryId,
          pickupArea: areaPickupSelected.title,
          xmlChannelProductIds: priceCombination.xmlChannelProductIds,
          levelName: level ? level.name : '',
          personCategoryName: personCategory ? personCategory.nameWithAges : '',
          changed: priceCombination.changed || false,
        };
      });

      priceCombinations.sort((a, b) => {
        if (a.levelName < b.levelName) {
          return -1;
        } else if (a.levelName > b.levelName) {
          return 1;
        }

        if (a.personCategoryName < b.personCategoryName) {
          return -1;
        } else if (a.personCategoryName > b.personCategoryName) {
          return 1;
        }

        return 0;
      });

      const lessonType = stateLessonTypes.find(
        stateLessonType => stateLessonType.id === statePrice.skiSchoolLessonTypeId,
      );
      const skiType = stateSkiTypes.find(
        stateSkiType => stateSkiType.id === statePrice.skiSchoolSkiTypeId,
      );

      let areaPickupSelected = state.pickupPlaces.items.find(
        areaPickup => areaPickup.id === statePrice.areaPickupId,
      );
      if (!areaPickupSelected) {
        areaPickupSelected = { title: 'No pickup place' };
      }

      return {
        id: statePrice.id,
        skiSchoolSeasonId: statePrice.skiSchoolSeasonId,
        skiSchoolLessonTypeId: statePrice.skiSchoolLessonTypeId,
        lessonTypeName: lessonType.name,
        skiSchoolSkiTypeId: statePrice.skiSchoolSkiTypeId,
        skiTypeName: skiType.name,
        numberOfPersons: statePrice.numberOfPersons,
        days: statePrice.days,
        durationMinutes: statePrice.durationMinutes,
        price: statePrice.price,
        commissionRatio: statePrice.commissionRatio,
        contributionRatio: statePrice.contributionRatio,
        webActive: statePrice.webActive,
        priceStartDays: statePrice.priceStartDays,
        areaPickup: areaPickupSelected.title,
        priceCombinations,
      };
    });

    return pricesWithNames.sort((a, b) => a.skiTypeName - b.skiTypeName);
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/ski/school/getters.js